import React from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import ApiService from "../../services/ApiService";
import Spinner from "@cs/components/src/Spinner";
import { useNavigate, useParams } from "react-router-dom";
import Banner from "@cs/components/src/Banner";
import { Trash2 } from "react-feather";
import Button from "@cs/components/src/Button";
import Section from "@cs/components/src/Section";
import styled from "styled-components";

interface Props {}

const Headers = styled.div`
  color: var(--gray100);
  line-height: 25px;
  margin-left: 10px;

  b {
    color: var(--gray300);
  }
`;

const Script = styled.div`
  margin-bottom: 20px;
`;

const LegacyComponentView = (_props: Props) => {
  const { componentId, systemDomainId } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["pluginInstance", systemDomainId, componentId],
    queryFn: async () => {
      const response = await ApiService.fetch<{
        data: {
          script: string;
          type: string;
          id: string;
          name: string;
          headers?: Record<string, string>;
          enabled: boolean;
        };
      }>("GET", `/api/plugin_instance/${systemDomainId}/legacy/${componentId}`);
      return response.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await ApiService.fetch(
        "DELETE",
        `/api/plugin_instance/${systemDomainId}/legacy/${componentId}`
      );
      queryClient.invalidateQueries({
        queryKey: ["pluginInstanceList", systemDomainId],
      });
      navigate(`..`);
    },
  });

  return isLoading ? (
    <Spinner />
  ) : isError ? (
    <Banner error="Error loading" />
  ) : !data ? (
    <></>
  ) : (
    <Section
      pad
      title={data.name}
      buttons={
        <Button
          label="Remove"
          icon={Trash2}
          type="danger"
          borderless
          onClick={() => {
            deleteMutation.mutate();
          }}
          loading={deleteMutation.isLoading}
        />
      }
    >
      {deleteMutation.isError && <Banner error="Error deleting components" />}
      <Script>
        {data.script} ({data.enabled ? "enabled" : "DISABLED"})
      </Script>
      {Object.keys(data.headers ?? {}).length ? (
        <Headers>
          {Object.keys(data.headers ?? {}).map((key) => (
            <div key={key}>
              <b>{key}</b>: {data.headers?.[key]}
            </div>
          ))}
        </Headers>
      ) : (
        <Banner placeholder="No headers" />
      )}
    </Section>
  );
};

export default LegacyComponentView;
